import { useMemo, useState, type FormEvent } from "react";
import { Eye, EyeOff, UserPlus } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Alert } from "../components/Alert";
import { Button } from "../components/Button";
import { Field, Input } from "../components/Field";
import { useAuth } from "../context/AuthContext";
import { ApiError, errorMessage } from "../lib/api";
import { cn } from "../lib/cn";
import { AuthLayout } from "./AuthLayout";

type FieldErrors = Record<string, string>;

function extractFieldErrors(error: unknown): FieldErrors {
  if (!(error instanceof ApiError)) return {};
  if (!Array.isArray(error.details)) return {};
  const result: FieldErrors = {};
  for (const item of error.details) {
    if (
      item &&
      typeof item === "object" &&
      "path" in item &&
      "message" in item &&
      typeof (item as { path: unknown }).path === "string" &&
      typeof (item as { message: unknown }).message === "string"
    ) {
      result[(item as { path: string }).path] = (
        item as { message: string }
      ).message;
    }
  }
  return result;
}

function passwordStrength(password: string): {
  score: number;
  label: string;
  tone: string;
} {
  let score = 0;
  if (password.length >= 10) score += 1;
  if (password.length >= 14) score += 1;
  if (/[a-zа-я]/i.test(password) && /\d/.test(password)) score += 1;
  if (/[^a-zа-я\d]/i.test(password)) score += 1;
  if (score <= 1) return { score, label: "Слабый", tone: "bg-rose-400" };
  if (score === 2) return { score, label: "Нормальный", tone: "bg-amber-400" };
  if (score === 3) return { score, label: "Хороший", tone: "bg-cyan-400" };
  return { score, label: "Надёжный", tone: "bg-emerald-400" };
}

export function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const strength = useMemo(() => passwordStrength(password), [password]);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setFieldErrors({});

    const nextErrors: FieldErrors = {};
    if (!email.trim()) nextErrors.email = "Введите email.";
    if (displayName.trim().length < 2)
      nextErrors.displayName = "Минимум 2 символа.";
    if (password.length < 10) nextErrors.password = "Минимум 10 символов.";
    if (password !== passwordConfirm)
      nextErrors.passwordConfirm = "Пароли не совпадают.";
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      return;
    }

    setSubmitting(true);
    try {
      await register({
        email: email.trim().toLowerCase(),
        displayName: displayName.trim(),
        password,
        passwordConfirm,
      });
      navigate("/dashboard", { replace: true });
    } catch (caught) {
      setFieldErrors(extractFieldErrors(caught));
      setError(errorMessage(caught));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title="Создать аккаунт"
      subtitle="Один аккаунт — доступ к своим и чужим активным комнатам."
      footer={
        <>
          Уже есть аккаунт?{" "}
          <Link
            to="/login"
            className="font-medium text-violet-300 transition-colors hover:text-violet-200"
          >
            Войти
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {error && <Alert tone="error">{error}</Alert>}
        <Field label="Email" htmlFor="email" error={fieldErrors.email}>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            invalid={Boolean(fieldErrors.email)}
            required
            autoFocus
          />
        </Field>
        <Field
          label="Отображаемое имя"
          htmlFor="displayName"
          error={fieldErrors.displayName}
          hint="Так вас увидят в комнатах"
        >
          <Input
            id="displayName"
            type="text"
            autoComplete="nickname"
            placeholder="Например, Алексей"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            invalid={Boolean(fieldErrors.displayName)}
            maxLength={40}
            required
          />
        </Field>
        <Field
          label="Пароль"
          htmlFor="password"
          error={fieldErrors.password}
          hint="Минимум 10 символов"
        >
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              placeholder="Придумайте пароль"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              invalid={Boolean(fieldErrors.password)}
              className="pr-11"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((value) => !value)}
              className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-zinc-500 transition-colors hover:text-zinc-200"
              aria-label={showPassword ? "Скрыть пароль" : "Показать пароль"}
            >
              {showPassword ? (
                <EyeOff className="size-4" />
              ) : (
                <Eye className="size-4" />
              )}
            </button>
          </div>
          {password && (
            <div className="mt-2 flex items-center gap-3">
              <div className="flex h-1 flex-1 gap-1">
                {[0, 1, 2, 3].map((index) => (
                  <span
                    key={index}
                    className={cn(
                      "h-full flex-1 rounded-full bg-white/[0.07] transition-colors",
                      index < strength.score && strength.tone,
                    )}
                  />
                ))}
              </div>
              <span className="w-20 text-right text-[10px] text-zinc-500">
                {strength.label}
              </span>
            </div>
          )}
        </Field>
        <Field
          label="Подтверждение пароля"
          htmlFor="passwordConfirm"
          error={fieldErrors.passwordConfirm}
        >
          <Input
            id="passwordConfirm"
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            placeholder="Повторите пароль"
            value={passwordConfirm}
            onChange={(event) => setPasswordConfirm(event.target.value)}
            invalid={Boolean(fieldErrors.passwordConfirm)}
            required
          />
        </Field>
        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="mt-2 w-full"
          loading={submitting}
        >
          {!submitting && <UserPlus className="size-4" />}
          Создать аккаунт
        </Button>
        <p className="text-center text-[11px] leading-relaxed text-zinc-600">
          Создавая аккаунт, вы используете self-hosted сервис для небольшой
          группы.
        </p>
      </form>
    </AuthLayout>
  );
}
